import type { ScheduleEntry } from '@reliance/types/models/Schedule';

export const SCHEDULE_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type ScheduleDayName = (typeof SCHEDULE_DAYS)[number];

/** One 30-minute slot in the editor grid (numeric time is HHMM with half-hour steps as :50). */
export type ScheduleBlockCell = {
  open: boolean;
  closed: boolean;
  selected: boolean;
  day: ScheduleDayName;
  time: number;
};

/** Parses `H:M[:S]` from the API into the internal HHMM-style number (e.g. 9:30 → 930). */
export const timeToNumeric = (time: string): number => {
  const parts = time.split(':');
  if (parseInt(parts[0], 10) === 23 && parseInt(parts[1], 10) === 59) {
    parts[0] = '24';
    parts[1] = '0';
  }
  return parseInt(`${parts[0]}${(`00${(parseInt(parts[1], 10) / 60) * 100}`).slice(-2)}`, 10);
};

/** Converts internal HHMM-style `numeric` back to `H:M:S` for API payloads. */
export const numericToTime = (numeric: number): string => {
  let minutes = ((numeric % 100) / 100) * 60;
  let hours = Math.floor(numeric / 100);
  let seconds = 0;
  if (hours === 24) {
    hours = 23;
    minutes = 59;
    seconds = 59;
  }
  return `${hours}:${(`00${minutes}`).slice(-2)}:${(`00${seconds}`).slice(-2)}`;
};

/** Expands each schedule entry into 30-minute step keys per weekday for lookup while building the grid. */
export const buildSelectedTimeMap = (entries: ScheduleEntry[] | null | undefined): Partial<Record<ScheduleDayName, number[]>> => {
  const scheduleData: Partial<Record<ScheduleDayName, number[]>> = {};
  if (entries == null) {
    return scheduleData;
  }
  for (const entry of entries) {
    let start = timeToNumeric(entry.start);
    const stop = timeToNumeric(entry.stop);
    const dayName = SCHEDULE_DAYS[entry.day];
    if (scheduleData[dayName] == null) {
      scheduleData[dayName] = [];
    }
    const dayArr = scheduleData[dayName]!;
    while (true) {
      dayArr.push(start);
      start += 50;
      if (start >= stop) {
        break;
      }
    }
  }
  return scheduleData;
};

/** Builds the full week grid: every slot in `[min, max]` per day, with `selected` from saved entries. */
export const buildEditModeBlocks = (
  entries: ScheduleEntry[] | null | undefined,
  minMax: { min: number; max: number }
): Record<ScheduleDayName, Record<number, ScheduleBlockCell>> => {
  const scheduleData = buildSelectedTimeMap(entries);
  const scheduleBlocks: Record<ScheduleDayName, Record<number, ScheduleBlockCell>> = {} as Record<
    ScheduleDayName,
    Record<number, ScheduleBlockCell>
  >;

  for (const day of SCHEDULE_DAYS) {
    const dayBlocks: Record<number, ScheduleBlockCell> = {};
    let time = minMax.min;
    while (true) {
      const selected = (scheduleData[day]?.indexOf(time) ?? -1) > -1;
      dayBlocks[time] = {
        open: true,
        closed: false,
        selected,
        day,
        time,
      };
      time += 50;
      if (time >= minMax.max) {
        break;
      }
    }
    scheduleBlocks[day] = dayBlocks;
  }
  return scheduleBlocks;
};

/** Collapses contiguous selected blocks per day into `{ day, start, stop }` ranges for save. */
export const calculateScheduleEntries = (
  blocks: Record<ScheduleDayName, Record<number, ScheduleBlockCell>>
): { day: number; start: string; stop: string }[] => {
  const schedule: { day: number; start: string; stop: string }[] = [];
  for (const day of SCHEDULE_DAYS) {
    const dayBlocks = blocks[day];
    let current: { day: number; start: string; stop: string } | null = null;
    const times = Object.keys(dayBlocks)
      .map((t) => parseInt(t, 10))
      .sort((a, b) => a - b);
    for (const time of times) {
      const block = dayBlocks[time];
      if (block.selected) {
        if (current === null) {
          current = { day: SCHEDULE_DAYS.indexOf(day), start: numericToTime(block.time), stop: numericToTime(block.time + 50) };
        } else {
          current.stop = numericToTime(block.time + 50);
        }
      } else if (current !== null) {
        schedule.push(current);
        current = null;
      }
    }
    if (current !== null) {
      schedule.push(current);
    }
  }
  return schedule;
};
